import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatCard, MatCardActions, MatCardContent } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatSpinner } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { differenceInMilliseconds, formatISO, isBefore, isWithinInterval, startOfDay } from 'date-fns';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Tools } from 'src/app/classes/shared/tools';
import { ALL_ROLES, GalleryComponent, TagDisplayConfig } from 'src/app/components/shared/gallery/gallery.component';
import { Attachment } from 'src/app/interfaces/api/attachment';
import { Metric } from 'src/app/interfaces/api/metric';
import { EnableTag } from 'src/app/interfaces/api/project';
import { ModuleType } from 'src/app/interfaces/shared/module';
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { SpinnerComponent } from 'ui-library';
import { Tracker } from '../../../../interfaces/api/tracker';

const LIMIT = 10;

@Component({
  selector: 'app-crane-cycles',
  templateUrl: './crane-cycles.component.html',
  styleUrls: ['./crane-cycles.component.scss'],
  imports: [
    SpinnerComponent,
    GalleryComponent,
    NoDataComponent,
    MatCard,
    MatCardContent,
    MatCardActions,
    MatSpinner,
    MatIcon,
    NgIf,
    NgFor,
    NgClass,
    DatePipe,
    TranslateModule
  ]
})
export class CraneCyclesComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChildren('metricElems') metricElems!: QueryList<ElementRef>;
  loading: boolean = false;


  @Input() trackerTypes$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  @Input() unitTypes$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  @Input() search!: BehaviorSubject<string>;
  @Input() selectedTagObs: BehaviorSubject<string> = new BehaviorSubject<string>("");
  @Input() availableResources: any[] = [];
  @Input() trackers: any[] = [];
  @Input() dateObs: BehaviorSubject<string> = new BehaviorSubject<string>(formatISO(startOfDay(new Date())));
  @Output() onParse: EventEmitter<any[]> = new EventEmitter();
  @Output() onScrollLoading: EventEmitter<boolean> = new EventEmitter();
  availableResources$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  socketMetricSub: any;
  socketAttachmentSub!: Subscription;
  resourcesSub!: Subscription | null;
  trackerSub!: Subscription | null;
  unitSub!: Subscription | null;
  selectedTagSub!: Subscription | null;
  datesSub!: Subscription | null;
  metricTypes = ["Coup de grue"];
  trackerTypes: string[] = [];
  unitTypes: string[] = [];
  currentSearch: string = "";

  metrics: any[] = [];
  attachments: any[] = [];

  currentProject: any = [];
  attachmentsMap: Map<string, any[]> = new Map();
  limit: number = LIMIT;
  noData = true;
  scrollLoading: boolean = false;
  offset: number = 0;
  mode: string = "tags";
  craneTags: any[] = [];
  zoneTags: any[] = [];

  scrollThreshold = 400;

  startDate = formatISO(startOfDay(new Date()));
  selectedTag: string | null = null;
  destroy$ = new Subject<void>();
  tagNames$: BehaviorSubject<any> = new BehaviorSubject(null);
  tagNames: Record<ModuleType, any[]> = {
    llm: [],
    risk: [],
    crane: [],
    zone: [],
    action: [],
    worker: []
  }
  user: any;
  tagDisplayConfig: TagDisplayConfig = {
    visibility: {
      crane: { ai: ['dev', 'admin'], manual: ALL_ROLES },
      zone: { ai: ['dev', 'admin'], manual: ALL_ROLES },
      action: { ai: ['dev', 'admin'], manual: ALL_ROLES },
      worker: { ai: ['dev', 'admin'], manual: ['dev', 'admin'] },
      risk: { ai: ['dev'], manual: ['dev', 'admin'] }
    }
  };

  constructor(
    private metricService: MetricService,
    private projectService: ProjectService,
    private attachmentService: AttachmentService,
    public dialog: MatDialog,
    private tools: Tools,
    private authService: AuthService
  ) {
    this.user = this.authService.currentUser();
  }

  async ngOnInit() {
    this.limit = LIMIT;
    this.attachments = [];
    this.metrics = [];
    this.offset = 0;
    this.trackerSub = this.trackerTypes$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.trackerTypes = data;
    });

    this.projectService.currentProject.pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.currentProject = res

      this.attachmentService.getTags().pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
        this.parseAllTagNames(res);
      });
    })

    // remove this as it probably related to tab 0
    this.unitSub = this.unitTypes$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.unitTypes = data;
    })

    this.resourcesSub = this.availableResources$.pipe(takeUntil(this.destroy$)).subscribe(async resources => {
      this.availableResources = resources;
    });
    this.startDate = this.dateObs.getValue();
    this.loading = true;
    this.getTagData();
  }

  ngAfterViewInit(): void {
    this.search.pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result !== this.currentSearch) {
        this.currentSearch = result;
        this.metrics = [];
        this.ngOnInit();
      }
    });

    this.datesSub = this.dateObs.pipe(takeUntil(this.destroy$)).subscribe(date => {
      if (this.loading) return;
      this.startDate = date;
      this.cleanup();
      this.ngOnInit();
    });

    this.selectedTagSub = this.selectedTagObs.pipe(takeUntil(this.destroy$)).subscribe(tag => {
      if (this.loading) return;
      this.selectedTag = tag;
      this.limit = LIMIT;
      this.cleanup();
      this.ngOnInit();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cleanup(): void {
    if (this.trackerSub)
      this.trackerSub.unsubscribe();

    if (this.unitSub)
      this.unitSub.unsubscribe();

    this.trackerSub = null;
    this.unitSub = null;
    this.resourcesSub = null;
  }


  parseTagNames(project: ModuleType, res: any[] = []) {
    const availableTags = res;
    if (availableTags.length) {
      this.tagNames[project] = availableTags.map(t => t.name || t.tagName).concat(this.currentProject.predictionTags);
      this.tagNames[project].sort((a, b) => a.localeCompare(b));
      if (this.currentProject.enableTags && this.currentProject.enableTags.length !== 0) {
        this.tagNames[project] = this.tagNames[project].filter(tagName => {
          const enableTag = this.currentProject.enableTags.find((enableTag: EnableTag) => enableTag.name === tagName);
          return enableTag ? enableTag.enabled : true;
        });
      }
    }
  }


  parseAllTagNames(tags: Record<ModuleType, any[]>) {
    this.parseTagNames("crane", tags["crane"]);
    this.parseTagNames("risk", tags["risk"]);
    ["worker", "zone", "action"].forEach((project: string) => {
      if (!tags[project as ModuleType]) return;
      this.tagNames[project as ModuleType] = tags[project as ModuleType].map(t => t.name || t.tagName);
      this.tagNames[project as ModuleType].sort((a, b) => a.localeCompare(b));
    });

    if (tags["action"])
      if (this.currentProject.enableTags && this.currentProject.enableTags.length !== 0) {
        this.tagNames.action = this.tagNames.action.filter(tagName => {
          const enableTag = this.currentProject.enableTags.find((enableTag: EnableTag) => enableTag.name === tagName);
          return enableTag ? enableTag.enabled : true;
        });
      }
  }

  async getTagData() {
    if (this.limit == 0) return;
    let craneCycleName = "Coup de grue";//<-to translate
    let query: any = { name: craneCycleName, startDate: this.startDate, limit: this.limit, offset: this.offset, sort: "asc" };
    
    if (this.selectedTag) {
      query.tag = this.selectedTag;
    }

    this.metricService.getMetrics(query).pipe(takeUntil(this.destroy$)).subscribe(async (metrics: Metric[]) => {

      //if no more metric set limit to 0 to stop calls     
      if (!metrics || metrics.length == 0) {
        this.scrollLoading = false;
        this.limit = 0;
        this.loading = false;
        this.noData = true;
        return;
      }

      this.noData = false;
      metrics = metrics.filter(m => m.payload && m.payload.startDate && m.payload.endDate).map(m => ({ ...m, attachments: [] }))
      this.offset += this.limit;

      if (this.selectedTag) {
        let promises: Promise<any>[] = [];

        metrics.forEach(m => {
          let promise = new Promise((resolve, reject) => {
            try {
              let attStartDate = new Date(m.payload.startDate!).toISOString();
              let attEndDate = new Date(m.payload.endDate!).toISOString();
              this.attachmentService.getAttachments({ startDate: attStartDate, endDate: attEndDate }).pipe(takeUntil(this.destroy$)).subscribe(att => {
                this.attachments = this.attachments.concat(att);
                resolve(this.attachments);
              });
            } catch (err) {
              console.error(err);
              reject(err);
            }
          });

          promises.push(promise);
        });

        await Promise.all(promises);

        if (this.metrics.length == 0)
          this.noData = true;

        this.scrollLoading = false;
        this.loading = false;
        this.handleMetrics(metrics, this.attachments);
      } else {
        let attStartDate = metrics[0]?.payload.startDate;
        let attEndDate = metrics[metrics.length - 1]?.payload.endDate;

        if (!attStartDate || !attEndDate) return;

        this.attachmentService.getAttachments({ startDate: attStartDate, endDate: attEndDate }).pipe(takeUntil(this.destroy$)).subscribe(att => {
          this.attachments = this.attachments.concat(att);
          // metrics.forEach(m => this.addAttachments(m, att));

          this.handleMetrics(metrics, att);
          if (this.metrics.length == 0)
            this.noData = true;
          this.scrollLoading = false;
          this.loading = false;
        })
      }
    })
  }

  addAttachments(attachments: Attachment[]) {
    attachments.forEach(a => {
      let metricIndex = this.metrics.findIndex(m => isWithinInterval(new Date(a.createdDate), { start: new Date(m.payload.startDate!), end: new Date(m.payload.endDate!) }))
      if (metricIndex >= 0)
        this.metrics[metricIndex].attachments.push(a);
      this.metrics[metricIndex].gallery = a;
    });
  }

  handleMetrics(metrics: Metric[], attachments: Attachment[]) {
    attachments.sort((a, b) => isBefore(new Date(a.createdDate), new Date(b.createdDate)) ? 1 : -1);

    attachments.forEach(a => {
      if (a.tags && a.tags.crane && a.tags.crane.find(p => p.probability == 1)) {
        a.tagged = true;
        a.prediction = a.tags.crane.filter(p => p.probability == 1).map(p => p.tagName).join(", ");
      }

      if (!a.customVisionIds)
        a.customVisionIds = { crane: "", risk: "", worker: "", action: "", zone: "" }
    })

    let handledMetrics = metrics.map(metric => {
      metric.payload.startDate = new Date(metric.payload.startDate!).toISOString();
      metric.payload.endDate = new Date(metric.payload.endDate!).toISOString();

      var ms = differenceInMilliseconds(new Date(metric.payload.endDate!), new Date(metric.payload.startDate!));
      var totalSeconds = Math.floor(ms / 1000);
      var hours = Math.floor(totalSeconds / 3600);
      var minutes = Math.floor((totalSeconds % 3600) / 60);
      var seconds = totalSeconds % 60;
      var s = `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

      metric.payload.duration = s;
      metric.payload.title = metric.payload.title || metric.payload["prediction"];
      let tracker = metric.tracker || metric.payload["tracker"];
      let trackerName = metric.trackerName || metric.payload["trackerName"];
      let unit = metric.unit || metric.payload["unit"];
      let unitName = metric.unitName || metric.payload["unitName"];

      metric.attachments = attachments.filter(a => 
        isWithinInterval(new Date(a.createdDate), { start: new Date(metric.payload.startDate!), end: new Date(metric.payload.endDate!) }) &&
        ((tracker == (a.tracker as Tracker)?.id) || (trackerName == (a.tracker as Tracker)?.name) || 
        (tracker == (a.tracker as Tracker)?.name) || (unit == a.unit) || (unitName == a.unit)))
        .sort((a,b)=> isBefore(a.createdDate, b.createdDate) ? -1 : 1);

      metric.gallery = {};

      if (metric.attachments.length)
        metric.gallery = metric.attachments[metric.attachments.length - 1];

      return metric;
    });

    this.metrics = this.metrics.concat(handledMetrics.filter(m => this.metrics.findIndex(met => met.id == m.id) == -1));
  }

  onThumbnailClick(metric: Metric, attachment: Attachment) {
    metric.gallery = attachment;
  }

  isTagged(attachment: Attachment): boolean {
    return !!attachment?.tags?.crane?.find(t => t.probability == 1);
  }


  onScroll($event: Event) {
    if (this.scrollLoading) return;

    const target = $event.target as HTMLElement;
    const scrollPosition = target.scrollTop;
    const elementHeight = target.offsetHeight;
    const scrollBottom = target.scrollHeight - (scrollPosition + elementHeight);

    if (scrollBottom <= this.scrollThreshold) {
      this.scrollLoading = true;
      this.getTagData();
    }
  }

  getUnitOrTrackerName(metric: Metric) {
    let unitName = metric.unitName || metric.payload?.["unitName"] || (this.tools.isHex(metric.unit as string) && metric.unit) || (this.tools.isHex(metric.payload?.["unit"] as string) && metric.payload?.["unit"]);
    
    if (unitName) 
      return unitName;

    let trackerName = metric.trackerName || metric.payload?.["trackerName"] || (this.tools.isHex(metric.tracker as string) && metric.tracker) || (this.tools.isHex(metric.payload?.["tracker"] as string) && metric.payload?.["tracker"]);
    
    if (trackerName) 
      return trackerName;

    return metric.unit || metric.payload?.["unit"] || metric.tracker || metric.payload?.["tracker"] || "No Ressource";
  }
}

